/**
 * One viewing, for an admin.
 *
 * `GET /api/admin/viewings/:id` has always existed and nothing ever called it: the rows on
 * `/admin/viewings` were inert, there was no route, and a deep link fell through to the dashboard. So
 * when a buyer rang about a viewing, the only way to see its history was the audit log.
 *
 * Read-only on purpose. A viewing is negotiated between a buyer, a broker and an owner; an admin
 * watches it and, where something has gone wrong, works through the people. Nothing here writes.
 */
import { useParams, Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Card, DateText, ErrorNotice, KV, PageHeader, Skeleton } from '@/components/ui';
import { useI18n } from '@/i18n';
import { api } from '@/lib/api';
import { RequestTimeline } from '@/features/shared/RequestParts';
import type { RequestEvent, VE } from '@/lib/types';
import { VeChip, waitingOn } from './common';

interface Detail {
  request: VE;
  proposal: { id: string; slotStart: string; proposedBy: string; createdAt: string } | null;
  proposals: { id: string; slotStart: string; proposedBy: string; createdAt: string }[];
  listing: { id: string; number: string; titleAr: string; priceHalalas: number } | null;
  address: { city: string; district: string; street: string | null };
  broker: { name: string; phone: string | null } | null;
  events: RequestEvent[];
  title: string;
  titleEn?: string;
}

export function AdminViewingDetail() {
  const { id } = useParams();
  const { t, localTitle } = useI18n();
  const q = useQuery({ queryKey: ['admin', 've', id], queryFn: () => api.get<Detail>(`/api/admin/viewings/${id}`) });
  if (q.isLoading) return <div className="flex flex-col gap-6"><Skeleton lines={8} /></div>;
  if (q.error || !q.data) return <ErrorNotice error={q.error} retry={() => void q.refetch()} labels={{ message: t('app.error'), retry: t('app.retry') }} />;
  const d = q.data;
  const v = d.request;
  const turn = waitingOn(v.status);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        crumbs={[{ to: '/admin', label: t('nav.dashboard') }, { to: '/admin/viewings', label: t('nav.viewings') }, { label: v.number }]}
        title={localTitle(d)}
        actions={<VeChip ve={v} />}
      />
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 items-start">
        <div className="flex flex-col gap-4">
          <Card>
            <h2 className="mb-3">{t('admin.viewings.proposedSlot')}</h2>
            <KV items={[
              { k: t('admin.viewings.proposedSlot'), v: <DateText at={v.confirmedSlotStart ?? d.proposal?.slotStart ?? null} kind="slot" /> },
              { k: t('admin.viewings.turnOn'), v: turn ? t(`status.turn.${turn}`) : '—' },
              ...(v.note ? [{ k: t('app.noteShort'), v: <span className="text-fg-2">«{v.note}»</span> }] : []),
              ...(v.declineReason ? [{ k: t('app.reason'), v: <span className="text-fg-2">«{v.declineReason}»</span> }] : []),
            ]} />
            {d.proposals.length > 1 ? (
              <>
                {/* Every time that was put forward, and by whom — a viewing is usually a negotiation
                    and the single "proposed slot" column on the list hides all but the last round. */}
                <h3 className="mt-5 mb-2">{t('admin.viewings.allProposals')}</h3>
                <ol className="flex flex-col gap-1.5">
                  {d.proposals.map((p) => (
                    <li key={p.id} className="text-[14px] text-fg-2 flex flex-wrap gap-2">
                      <DateText at={p.slotStart} kind="slot" />
                      <span className="text-muted">· {t(`status.turn.${p.proposedBy}`)}</span>
                    </li>
                  ))}
                </ol>
              </>
            ) : null}
          </Card>
          <Card>
            <h2 className="mb-4">{t('admin.request.updates')}</h2>
            {d.events.length ? <RequestTimeline events={d.events} viewer="admin" /> : <p className="text-muted text-[14px]">{t('broker.request.noUpdatesYet')}</p>}
          </Card>
        </div>
        <aside className="flex flex-col gap-3">
          <Card>
            <h2 className="mb-2">{t('app.property')}</h2>
            <p className="text-[14px] text-fg-2">{d.address.district + '، ' + d.address.city}</p>
            {d.address.street ? <p className="text-[14px] text-muted mt-1">{d.address.street}</p> : null}
            {d.listing ? <Link to={`/admin/broker-requests/${v.brokerRequestId ?? ''}`} className="text-[14px] mt-3 inline-block num">{d.listing.number}</Link> : null}
          </Card>
          <Card>
            <h2 className="mb-2">{t('app.parties')}</h2>
            <KV items={[
              { k: t('app.buyer'), v: v.buyerName ?? '—' },
              { k: t('app.broker'), v: d.broker?.name ?? v.brokerName ?? '—' },
            ]} />
          </Card>
        </aside>
      </div>
    </div>
  );
}
